import React, { useState, useEffect, useRef } from 'react';
import styles from './LocalArea.module.css';
import YouMayAlsoLike from '../components/YouMayAlsoLike';

const LocalArea = ({ onNavigate }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const pageRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Local area images for carousel
  const areaImages = [
    { src: '/website_images/area_castle.jpg', alt: 'Harlech Castle and surrounding area' },
    { src: '/website_images/beach_view.jpg', alt: 'Beautiful beach view near Harlech' },
    { src: '/website_images/area_beach1.jpg', alt: 'Harlech beach area view 1' },
    { src: '/website_images/area_beach2.jpg', alt: 'Harlech beach area view 2' },
    { src: '/website_images/area_beach3.jpg', alt: 'Harlech beach area view 3' },
    { src: '/website_images/area_beach4.jpg', alt: 'Harlech beach area view 4' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === areaImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? areaImages.length - 1 : prevIndex - 1
    );
  };

  return (
    <div ref={pageRef} className={styles['local-area-page']}>
      <div className={styles.container}>
        <h1 className={styles['page-title']}>Local Area</h1>
        
        {/* Introduction Section */}
        <div className={styles['intro-section']}>
          <p>
            Harlech is a treasure trove of history and natural beauty, nestled in the heart of 
            Snowdonia National Park. From our medieval castle that overlooks magnificent beaches 
            to mountain trails and charming local shops, there's something magical waiting around 
            every corner.
          </p>
          <p>
            Whether you're seeking adventure in the great outdoors, fascinating history, or simply 
            want to relax and soak in the stunning Welsh landscape, Harlech and the surrounding 
            area offers unforgettable experiences for every type of visitor.
          </p>
        </div>

        {/* Image Carousel */}
        <div className={styles['carousel-container']} data-testid="image-carousel">
          <button className={styles['carousel-btn']} onClick={prevImage} aria-label="Previous image">
            ‹
          </button>
          <div className={styles.carousel}>
            <img 
              src={areaImages[currentImageIndex].src}
              alt={areaImages[currentImageIndex].alt}
              className={styles['carousel-image']}
            />
          </div>
          <button className={styles['carousel-btn']} onClick={nextImage} aria-label="Next image">
            ›
          </button>
        </div>

        {/* Main Attractions Section */}
        <div className={styles['attractions-section']}>
          <h2 className={styles['section-title']}>Must-See Attractions</h2>
          
          <div className={styles['attraction-grid']}>
            <div className={styles['attraction-card']}>
              <h3>Harlech Castle</h3>
              <p>
                Our crown jewel - a UNESCO World Heritage Site that crowns a sheer rocky crag. 
                Built by Edward I and completed in just seven years, this spectacular fortress 
                offers breathtaking views over the coast and mountains. Dog-friendly grounds 
                with an outdoor café terrace.
              </p>
            </div>
            
            <div className={styles['attraction-card']}>
              <h3>Harlech Beach</h3>
              <p>
                A vast, peaceful stretch of golden sand overlooked by the mighty castle. Perfect 
                for long walks, surfing, paddleboarding, and kayaking. The beautiful sand dunes 
                are a National Nature Reserve and Site of Special Scientific Interest.
              </p>
            </div>
            
            <div className={styles['attraction-card']}>
              <h3>Snowdonia National Park</h3>
              <p>
                On our doorstep lies some of Wales' most spectacular mountain scenery. From gentle 
                family walks to challenging hikes, the park offers magnificent views, diverse wildlife, 
                and outdoor adventures for every ability level.
              </p>
            </div>
          </div>
        </div>

        {/* Walking Trails Section */}
        <div className={styles['walking-section']}>
          <h2 className={styles['section-title']}>Walking & Hiking Trails</h2>
          
          <div className={styles['trail-list']}>
            <div className={styles['trail-item']}>
              <h4>Branwen Walk</h4>
              <p><strong>Distance:</strong> 1.5 miles (2.4km) | <strong>Duration:</strong> 1 hour</p>
              <p>Perfect for families - explore Harlech Castle, town, beach and dunes plus a section of the Wales Coast Path.</p>
            </div>
            
            <div className={styles['trail-item']}>
              <h4>Meirion's Story Trail</h4>
              <p><strong>Family-friendly:</strong> Interactive trail with storyboards</p>
              <p>Bring local legends to life with unique hand-crafted chair sculptures winding through town and castle grounds.</p>
            </div>
            
            <div className={styles['trail-item']}>
              <h4>Wales Coast Path to Llandanwg</h4>
              <p><strong>Distance:</strong> 2.8 miles (4.4km) one way | <strong>Moderate</strong></p>
              <p>Follow the coast south along Harlech beach, then climb the zig-zag cliff path for fantastic coastal views.</p>
            </div>
            
            <div className={styles['trail-item']}>
              <h4>Wales Coast Path Extended</h4>
              <p><strong>Distance:</strong> 12 miles (19km) | <strong>Full day adventure</strong></p>
              <p>Long-distance coastal walk passing dunes, salt marshes, and wide beaches with stunning mountain backdrops.</p>
            </div>
          </div>
        </div>

        {/* Practical Information Section */}
        <div className={styles['practical-section']}>
          <h2 className={styles['section-title']}>Getting Around</h2>
          
          <div className={styles['transport-info']}>
            <div className={styles['transport-item']}>
              <h4>Public Transport</h4>
              <p>Bus routes 38 & 39 (Route 2 on Sunday) connect Barmouth-Harlech-Porthmadog. The bus stop is just 150m from the castle.</p>
            </div>
            
            <div className={styles['transport-item']}>
              <h4>Cycling</h4>
              <p>National Cycle Network Route 8 passes through Harlech, perfect for exploring the coast and countryside on two wheels.</p>
            </div>
            
            <div className={styles['transport-item']}>
              <h4>Beach Access</h4>
              <p>Easy beach access via a path from the Beach Road car park. Ample pay & display parking available and public toilets. Note final part of the path is across soft sand dunes that are not suitable for wheelchairs or buggies.</p>
            </div>
          </div>
        </div>
      </div>
      <YouMayAlsoLike currentPage="local-area" onNavigate={onNavigate} />
    </div>
  );
};

export default LocalArea;